import React from 'react';
import { graphql, PageRendererProps } from 'gatsby';

import createOpenGraphTags from '@repo/utils/createOpenGraphTags';
import PageWrapper from '@repo/ui-components/page-wrapper/PageWrapper';
import BlockBuilder from '@repo/ui-components/sanity/BlockBuilder';
import Button from '@repo/ui-components/button/Button';
import BlockBuilderAccordion from '@repo/ui-components/sanity/types/BlockBuilderAccordion';
import FacebookIcon from '@repo/ui-components/svgs/bs-facebook.svg';
import { GetSanityDealerTemplateQuery } from '@repo/models/gatsby-graphql-types';

import Layout from '@components/layout';
import AlertMessage from '@components/dealer/AlertMessage';
import DealerAddress from '@components/dealer/DealerAddress';
import LinkButtons from '@components/dealer/LinkButtons';
import OpeningHours from '@components/dealer/OpeningHours';
import getInternalLink from '@components/utils/getInternalLink';

import { SEOMetadata as SEOMetadataProps } from '../models/SEOMetadata';

interface DealerDetailsContainerTypes {
  isCompanyOwned?: boolean;
  hasStorefront?: boolean;
}

interface DealerPageProps extends PageRendererProps {
  data: GetSanityDealerTemplateQuery;
}

const DealerDetailsContainer = ({
  children,
  isCompanyOwned,
  hasStorefront,
}: DealerDetailsContainerTypes & { children: React.ReactNode }) => {
  const showFullWidth = isCompanyOwned && !hasStorefront;

  const responsiveWidth = showFullWidth
    ? 'w-full'
    : 'md:[width:calc(50%-0.5rem)] lg:[width:calc(60%-0.5rem)]';

  return (
    <div
      className={`bg-brand text-white p-4 w-full ${responsiveWidth} 
        [&_h1]:mb-0 [&_h1+p]:mb-6 [&_p]:m-0`}
    >
      {children}
    </div>
  );
};

const getDealerCountry = (country: string | null): string => {
  if (!country) return '';
  return country.toLowerCase();
};

const getLocationOrigin = (): string | undefined =>
  typeof window !== 'undefined' ? window.location.origin : undefined;

const isCanada = (country: string | null): boolean => {
  const dealerCountry = getDealerCountry(country);
  return (
    dealerCountry === 'ca' ||
    dealerCountry === 'can' ||
    dealerCountry === 'canada'
  );
};

const getCanonicalUrl = (slug: string, country: string | null): string => {
  const locationOrigin = getLocationOrigin() || '';
  return isCanada(country)
    ? `${locationOrigin}/en-ca/dealer/${slug}`
    : `${locationOrigin}/dealer/${slug}`;
};

function createMetaTags(
  SEOMetadata: SEOMetadataProps,
  name: string,
  canonicalUrl: string
) {
  return {
    title: SEOMetadata?.title || name,
    description: SEOMetadata?.description || '',
    canonicalUrl,
    noIndex: SEOMetadata?.noIndex,
    noFollow: SEOMetadata?.noFollow,
    meta: createOpenGraphTags(
      {
        title: SEOMetadata?.ogTitle || SEOMetadata?.title || name,
        description:
          SEOMetadata?.ogDescription || SEOMetadata?.description || '',
        url:
          SEOMetadata?.ogUrl ||
          SEOMetadata?.canonicalUrl ||
          canonicalUrl ||
          '/',
        image: SEOMetadata?.ogImage,
      },
      'invisiblefence'
    ),
  };
}

// Function to convert 12-hour time format to 24-hour time format
const convertTo24HourFormat = (timeString: string): string => {
  const today = new Date();
  const dateTime = new Date(
    `${today.getFullYear()}-${
      today.getMonth() + 1
    }-${today.getDate()} ${timeString}`
  );

  const hours = dateTime.getHours().toString().padStart(2, '0');
  const minutes = dateTime.getMinutes().toString().padStart(2, '0');

  return `${hours}:${minutes}`;
};

const convertToOpeningHours = (hours: Record<string, any>): string[] => {
  const openingHoursArray =
    Object.values(hours)
      ?.map(day =>
        day?.isOpen
          ? `${day?.dayOfWeek
              ?.substring(0, 1)
              ?.toUpperCase()}${day?.dayOfWeek?.substring(
              1,
              2
            )} ${convertTo24HourFormat(
              day.openingHours
            )}-${convertTo24HourFormat(day.closingHours)}`
          : ''
      )
      ?.filter(d => d) || [];

  return openingHoursArray;
};

const convertToAddressCountry = country => {
  const normalizedCountry = country?.toLowerCase();
  let addressCountry = 'US';

  if (
    normalizedCountry === 'canada' ||
    normalizedCountry === 'can' ||
    normalizedCountry === 'ca'
  ) {
    addressCountry = 'CA';
  }

  if (normalizedCountry === 'great britain') {
    addressCountry = 'GB';
  }

  if (normalizedCountry === 'bermuda') {
    addressCountry = 'BM';
  }

  return addressCountry;
};

function DealerPage({ data, location }: Readonly<DealerPageProps>) {
  const {
    SEOMetadata,
    name,
    slug,
    mainServiceArea,
    phoneNumber,
    addressLine1,
    addressLine2,
    addressLine3,
    city,
    state,
    zip,
    country,
    hours,
    additionalInfo,
    alertMessage,
    areasOfService,
    facebookLink,
    isCompanyOwned,
  } = data.sanityDealer;

  const { socialMedia, dealerBoilerplate, codCallHours } =
    data.sanitySiteSettings;

  const canonicalUrl =
    SEOMetadata?.canonicalUrl ?? getCanonicalUrl(slug.current, country);

  const headTagData = createMetaTags(SEOMetadata, name, canonicalUrl);

  let streetAddress = addressLine1;

  if (addressLine2) {
    streetAddress = `${streetAddress}, ${addressLine2}`;
  }

  const areaServed = areasOfService?.[0]?.children?.[0]?.text?.split(', ');

  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name,
    description: `Our pet containment system uses satellite GPS to map your pet's boundaries, eliminating wire-break worries on any property, regardless of size or terrain.`,
    image: [`/images/ifb-square.png`],
    telephone: phoneNumber,
    url: canonicalUrl,
    openingHours: convertToOpeningHours(hours),
    areaServed: areaServed?.map(area => ({
      '@type': 'City',
      name: area,
    })),
    address: {
      '@type': 'PostalAddress',
      addressCountry: convertToAddressCountry(country),
      postalCode: zip,
      streetAddress,
      addressLocality: city,
      addressRegion: state,
    },
    brand: 'Invisible Fence®',
    currenciesAccepted: isCanada(country) ? 'CAD' : 'USD',
    paymentAccepted: 'Cash, Credit Card',
    priceRange: '$$',
  };
  return (
    <Layout
      location={location}
      isDealerPage
      headTagData={headTagData}
      fullWidth
      scriptsData={[
        <script id="dealer-schema" type="application/ld+json" key="dealer-key">
          {JSON.stringify(localBusinessSchema)}
        </script>,
      ]}
    >
      <PageWrapper padding="1.625rem">
        <div className="flex flex-wrap gap-4">
          <DealerDetailsContainer
            isCompanyOwned={isCompanyOwned}
            hasStorefront={!!(addressLine1 || addressLine2 || addressLine3)}
          >
            {name && <h1 className="text-white mb-4">{name}</h1>}
            {mainServiceArea && <p>Serving {mainServiceArea}</p>}
            <DealerAddress
              addressLine1={addressLine1}
              addressLine2={addressLine2}
              addressLine3={addressLine3}
              city={city}
              state={state}
              zip={zip}
              country={country}
              phoneNumber={phoneNumber}
              isCompanyOwned={isCompanyOwned}
              codCallHours={codCallHours}
            />
          </DealerDetailsContainer>

          <OpeningHours
            hours={hours}
            additionalInfo={additionalInfo}
            isCompanyOwned={isCompanyOwned}
            hasStorefront={!!(addressLine1 || addressLine2 || addressLine3)}
          />

          {alertMessage?.length > 0 && (
            <AlertMessage
              alertMessage={alertMessage}
              getInternalLink={getInternalLink}
            />
          )}

          <LinkButtons />

          {areasOfService?.length > 0 && (
            <div className="w-full [&>div]:my-10">
              <BlockBuilderAccordion
                value={{
                  items: [
                    {
                      _key: 'area-of-service',
                      headline: 'Areas of Service',
                      copy: areasOfService,
                    },
                  ],
                }}
                getInternalLink={getInternalLink}
              />
            </div>
          )}

          <div className="flex gap-4 flex-wrap lg:flex-nowrap">
            <div className="lg:w-[70%]">
              <BlockBuilder
                blocks={dealerBoilerplate?.blocks}
                getInternalLink={getInternalLink}
                Button={Button}
              />
            </div>

            <div className="lg:w-[30%]">
              <p>
                Want expert tips and tricks to enjoying your pet? Go fetch our
                latest <a href="https://blog.invisiblefence.com/">blog</a>.
              </p>
              <div className="flex gap-4">
                {(facebookLink || socialMedia?.facebookBool) && (
                  <>
                    <FacebookIcon fill="#3b5998" />
                    <p>
                      You&apos;re part of the neighborhood. Follow us on{' '}
                      <a
                        href={
                          facebookLink ||
                          (socialMedia?.facebookBool &&
                            socialMedia?.facebookUrl)
                        }
                        target="_blank"
                        rel="noreferrer"
                      >
                        Facebook.
                      </a>
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </PageWrapper>
    </Layout>
  );
}

export const query = graphql`
  query GetSanityDealerTemplate($id: String!) {
    sanityDealer(id: { eq: $id }) {
      SEOMetadata {
        title
        description
        canonicalUrl
        ogTitle
        ogDescription
        ogUrl
        ogImage {
          asset {
            url
          }
        }
        noIndex
        noFollow
      }
      name
      slug {
        current
      }
      dealerID
      mainServiceArea
      phoneNumber
      addressLine1
      addressLine2
      addressLine3
      city
      state
      zip
      country
      hours {
        dayOfWeek
        isOpen
        openingHours
        closingHours
      }
      additionalInfo
      alertMessage: _rawAlertMessage(resolveReferences: { maxDepth: 3 })
      areasOfService: _rawAreasOfService(resolveReferences: { maxDepth: 3 })
      facebookLink
      isCompanyOwned
    }
    sanitySiteSettings(_id: { eq: "siteSettings-ifb" }) {
      socialMedia {
        facebookBool
        facebookUrl
      }
      dealerBoilerplate: _rawDealerBoilerplate(
        resolveReferences: { maxDepth: 5 }
      )
      codCallHours
    }
  }
`;

export default DealerPage;
